import { lazy, type ReactElement } from 'react'
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'

import { AppShell } from '@/components/layout/AppShell'
import { RootLayout } from '@/components/layout/RootLayout'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { RequireOnboarding } from '@/components/auth/RequireOnboarding'
import { ModuleBoundary } from '@/components/common/ModuleBoundary'
import { ErrorFallback } from '@/components/common/ErrorFallback'
import { SkeletonLoader } from '@/components/common/SkeletonLoader'
import MapPage from '@/pages/MapPage.tsx'

// Route-level code splitting — each page is its own chunk.
//const HomePage = lazy(() => import('@/pages/HomePage'))
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const DiscoverPage = lazy(() => import('@/pages/DiscoverPage'))
const NewsDetailPage = lazy(() => import('@/pages/NewsDetailPage'))
const EventDetailPage = lazy(() => import('@/pages/EventDetailPage'))
const ScholarshipDetailPage = lazy(() => import('@/pages/ScholarshipDetailPage'))
const DoItOnlinePage = lazy(() => import('@/pages/DoItOnlinePage'))
const DoItOnlineTopicPage = lazy(() => import('@/pages/DoItOnlineTopicPage'))
const DoItOnlineArticlePage = lazy(() => import('@/pages/DoItOnlineArticlePage'))
const SchedulePage = lazy(() => import('@/pages/SchedulePage'))
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const WelcomePage = lazy(() => import('@/pages/WelcomePage'))
const DecisionPage = lazy(() => import('@/pages/DecisionPage'))
const AuthCallbackPage = lazy(() => import('@/pages/AuthCallbackPage'))
const ForbiddenPage = lazy(() => import('@/pages/ForbiddenPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

function RedirectToDiscover(): ReactElement {
  const { search, hash } = useLocation()
  return <Navigate to={{ pathname: '/discover', search, hash }} replace />
}

const routerError = (
  <ErrorFallback
    error={new Error('Router error')}
    resetErrorBoundary={() => {
      globalThis.location.assign('/')
    }}
  />
)

export const router = createBrowserRouter([
  {
    // Pathless root: global overlays (cookie consent) live here, so they show on every page.
    element: <RootLayout />,
    children: [
      // Full-bleed screens: they bring their own layout (canvas + logo lockup).
      {
        path: '/login',
        element: (
          <ModuleBoundary name="login" fallback={null}>
            <LoginPage />
          </ModuleBoundary>
        ),
        errorElement: routerError,
      },
      {
        path: '/auth/callback',
        element: (
          <ModuleBoundary name="auth-callback" fallback={null}>
            <AuthCallbackPage />
          </ModuleBoundary>
        ),
      },
      {
        path: '/decision',
        element: (
          <RequireAuth>
            <ModuleBoundary name="decision" fallback={null}>
              <DecisionPage />
            </ModuleBoundary>
          </RequireAuth>
        ),
      },
      {
        path: '/welcome',
        element: (
          <RequireAuth>
            <ModuleBoundary name="welcome" fallback={null}>
              <WelcomePage />
            </ModuleBoundary>
          </RequireAuth>
        ),
      },

      // The signed-in application shell.
      {
        element: (
          <ModuleBoundary name="app" fallback={<SkeletonLoader />}>
            <AppShell />
          </ModuleBoundary>
        ),
        errorElement: routerError,
        children: [
          {
            index: true,
            element: <RedirectToDiscover />,
          },
          {
            path: 'dashboard',
            element: (
              <RequireAuth>
                <RequireOnboarding>
                  {/* Feature boundary: this module can fail without killing the route. */}
                  <ModuleBoundary name="dashboard">
                    <DashboardPage />
                  </ModuleBoundary>
                </RequireOnboarding>
              </RequireAuth>
            ),
          },
          // Discover and its two detail screens. `/news` and `/events` land on the
          // matching Discover category so the sidebar entries keep working.
          {
            path: 'discover',
            element: (
              <RequireAuth>
                <RequireOnboarding>
                  <ModuleBoundary name="discover">
                    <DiscoverPage />
                  </ModuleBoundary>
                </RequireOnboarding>
              </RequireAuth>
            ),
          },
          { path: 'explore', element: <Navigate to="/discover" replace /> },
          { path: 'news', element: <Navigate to="/discover?category=news" replace /> },
          { path: 'events', element: <Navigate to="/discover?category=events" replace /> },
          {
            path: 'student-life/scholarships',
            element: <Navigate to="/discover?category=scholarships" replace />,
          },
          {
            path: 'news/:newsId',
            element: (
              <RequireAuth>
                <RequireOnboarding>
                  <ModuleBoundary name="news-detail">
                    <NewsDetailPage />
                  </ModuleBoundary>
                </RequireOnboarding>
              </RequireAuth>
            ),
          },
          {
            path: 'scholarships/:scholarshipId',
            element: (
              <RequireAuth>
                <RequireOnboarding>
                  <ModuleBoundary name="scholarship-detail">
                    <ScholarshipDetailPage />
                  </ModuleBoundary>
                </RequireOnboarding>
              </RequireAuth>
            ),
          },
          {
            path: 'events/:eventId',
            element: (
              <RequireAuth>
                <RequireOnboarding>
                  <ModuleBoundary name="event-detail">
                    <EventDetailPage />
                  </ModuleBoundary>
                </RequireOnboarding>
              </RequireAuth>
            ),
          },
          // Do it Online — the knowledge base: topics, one topic, one article.
          {
            path: 'do-it-online',
            element: (
              <RequireAuth>
                <RequireOnboarding>
                  <ModuleBoundary name="do-it-online">
                    <DoItOnlinePage />
                  </ModuleBoundary>
                </RequireOnboarding>
              </RequireAuth>
            ),
          },
          {
            path: 'do-it-online/:topicId',
            element: (
              <RequireAuth>
                <RequireOnboarding>
                  <ModuleBoundary name="do-it-online-topic">
                    <DoItOnlineTopicPage />
                  </ModuleBoundary>
                </RequireOnboarding>
              </RequireAuth>
            ),
          },
          {
            path: 'do-it-online/:topicId/:articleId',
            element: (
              <RequireAuth>
                <RequireOnboarding>
                  <ModuleBoundary name="do-it-online-article">
                    <DoItOnlineArticlePage />
                  </ModuleBoundary>
                </RequireOnboarding>
              </RequireAuth>
            ),
          },
          // Schedule — the academic calendar, the sidebar's "Schedule" entry.
          {
            path: 'studies/academic-calendar',
            element: (
              <RequireAuth>
                <RequireOnboarding>
                  <ModuleBoundary name="schedule">
                    <SchedulePage />
                  </ModuleBoundary>
                </RequireOnboarding>
              </RequireAuth>
            ),
          },
          {
            path: 'map',
            element: <MapPage />,
          },
          { path: 'forbidden', element: <ForbiddenPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
])
